import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import PublicLayout from "@/layouts/PublicLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, CheckCircle2, ArrowLeft, AlertCircle } from "lucide-react";
import { useLang } from "@/context/LangContext";

export default function ResetPassword() {
  const { t } = useLang();
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const navigate = useNavigate();
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!token) {
    return (
      <PublicLayout>
        <section className="mx-auto max-w-md px-6 py-16">
          <div className="rounded-3xl border border-rose-100 bg-white p-8 text-center shadow-sm">
            <AlertCircle className="mx-auto h-12 w-12 text-rose-500" />
            <h1 className="mt-4 font-serif text-2xl text-navy">Lien invalide</h1>
            <p className="mt-2 text-sm text-neutral-600">{t("comp_resetpassword_90")}</p>
            <Link to="/forgot-password" className="mt-4 inline-block rounded-full bg-wine-500 px-5 py-2 text-xs font-bold text-white">{t("comp_resetpassword_91")}</Link>
          </div>
        </section>
      </PublicLayout>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) { toast.error("Le mot de passe doit faire au moins 8 caractères"); return; }
    if (pwd !== pwd2) { toast.error("Les mots de passe ne correspondent pas"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setLoading(false);
  };

  return (
    <PublicLayout>
      <section className="mx-auto max-w-md px-6 py-16">
        <div className="rounded-3xl border border-wine-100 bg-white p-8 shadow-sm">
          {!done ? (
            <>
              <div className="grid h-12 w-12 place-items-center rounded-2xl bg-wine-100"><KeyRound className="h-6 w-6 text-wine-600" /></div>
              <h1 className="mt-4 font-serif text-3xl text-navy">{t("comp_resetpassword_92")}</h1>
              <p className="mt-2 text-sm text-neutral-600">{t("comp_resetpassword_93")}</p>
              <form onSubmit={submit} className="mt-6 space-y-3">
                <input type="password" required minLength={8} value={pwd} onChange={(e) => setPwd(e.target.value)}
                       placeholder={t("comp_resetpassword_92")}
                       className="w-full rounded-xl border border-neutral-200 px-4 py-2.5 text-sm focus:border-wine-400 focus:outline-none"
                       data-testid="reset-pwd" />
                <input type="password" required minLength={8} value={pwd2} onChange={(e) => setPwd2(e.target.value)}
                       placeholder={t("comp_resetpassword_94")}
                       className="w-full rounded-xl border border-neutral-200 px-4 py-2.5 text-sm focus:border-wine-400 focus:outline-none"
                       data-testid="reset-pwd2" />
                <button type="submit" disabled={loading}
                        className="w-full rounded-full bg-wine-500 py-2.5 text-sm font-bold text-white hover:bg-wine-600 disabled:opacity-60"
                        data-testid="reset-submit">
                  {loading ? "Enregistrement…" : "Définir mon mot de passe"}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center" data-testid="reset-done">
              <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" />
              <h1 className="mt-4 font-serif text-3xl text-navy">{t("comp_resetpassword_95")}</h1>
              <p className="mt-3 text-sm text-neutral-600">{t("comp_resetpassword_96")}</p>
            </div>
          )}
          <div className="mt-6 text-center">
            <Link to="/login" className="inline-flex items-center gap-1.5 text-xs font-semibold text-wine-700 hover:underline">
              <ArrowLeft className="h-3.5 w-3.5" /> Retour à la connexion
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
